import { useEffect, useState, useRef } from 'react';

import { Severity, ErrorType } from '@atlassian/growth-commons-error-analytics';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { useExperimentErrorAnalytics } from '../utils/useExperimentErrorAnalytics';

export const useUpgradeFreePoolTargetExperimentCohort = ({
	isEligible,
	analyticsAttributes,
}: {
	isEligible?: boolean;
	analyticsAttributes: Record<string, any>;
}) => {
	const { trackError, fireExperimentExposureEvent } = useExperimentErrorAnalytics({
		defaultAdditionalAttributes: analyticsAttributes,
	});

	const [aaCohort, setAACohort] = useState<string>('not-enrolled');
	const [abCohort, setABCohort] = useState<string>('not-enrolled');

	// Track if analytics events have been fired to prevent duplicates
	const aaEventFiredRef = useRef<boolean>(false);
	const abEventFiredRef = useRef<boolean>(false);

	useEffect(() => {
		if (isEligible && fg('twcg-206_confluence_upgrade_permanent_free_fg')) {
			try {
				setAACohort(
					expVal('twcg-206_confluence_upgrade_permanent_free_aa', 'cohort', 'not-enrolled'),
				);
				setABCohort(expVal('twcg-206_confluence_upgrade_permanent_free', 'cohort', 'not-enrolled'));
			} catch (error) {
				trackError({
					errorType: ErrorType.GENERIC,
					errorMessage:
						error instanceof Error ? error.message : 'Experiment cohort determination failed',
					severity: Severity.MEDIUM,
					source: 'useUpgradeFreePoolTargetExperimentCohort_cohortDetermination',
					cohort: 'not-enrolled',
					additionalAttributes: {
						isEligible,
						featureGateEnabled: fg('twcg-206_confluence_upgrade_permanent_free_fg'),
						errorStack: error instanceof Error ? error.stack : undefined,
						errorName: error instanceof Error ? error.name : undefined,
					},
				});
				// Fall back to not-enrolled on error
				setAACohort('not-enrolled');
				setABCohort('not-enrolled');
			}
		}
	}, [isEligible, trackError]);

	useEffect(() => {
		if (aaCohort !== 'not-enrolled' && !aaEventFiredRef.current) {
			const success = fireExperimentExposureEvent({
				cohort: aaCohort,
				experimentId: 'twcg-206_confluence_upgrade_permanent_free_aa',
				actionSubjectId: 'twcg-206_confluence_upgrade_permanent_free_aa-exposed',
				source: 'twcg-206_confluence_upgrade_permanent_free_aa',
			});
			if (success) {
				aaEventFiredRef.current = true;
			}
		}

		if (abCohort !== 'not-enrolled' && !abEventFiredRef.current) {
			const success = fireExperimentExposureEvent({
				cohort: abCohort,
				experimentId: 'twcg-206_confluence_upgrade_permanent_free',
				actionSubjectId: 'twcg-206_confluence_upgrade_permanent_free_exposed',
				source: 'twcg-206_confluence_upgrade_permanent_free',
			});
			if (success) {
				abEventFiredRef.current = true;
			}
		}
	}, [aaCohort, abCohort, fireExperimentExposureEvent]);

	return { abCohort };
};
